import { useEffect, useRef, type RefObject } from 'react';

/**
 * Generic custom hook to handle auto-scrolling to elements based on an active index.
 * Automatically scrolls to the next/previous element when the active index changes.
 *
 * @param currentIndex - The index of the currently active element
 * @param elementRefs - Array of refs to scrollable elements
 * @param scrollOffset - Number of elements to scroll ahead/behind (default: 1)
 */
export const useAutoScroll = <T extends HTMLElement>(
    currentIndex: number,
    elementRefs: RefObject<(T | null)[]>,
    scrollOffset: number = 1,
) => {
    const previousIndexRef = useRef<number>(-1);

    useEffect(() => {
        const scrollDirection = currentIndex - previousIndexRef.current;

        let targetIndex = currentIndex;
        if (scrollDirection > 0) {
            targetIndex = currentIndex + scrollOffset;
        } else if (scrollDirection < 0) {
            targetIndex = currentIndex - scrollOffset;
        }

        const elements = elementRefs.current;
        if (!elements) {
            return;
        }

        const targetElement = elements[targetIndex] || elements[currentIndex];

        if (targetElement && typeof targetElement.scrollIntoView === 'function') {
            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }

        previousIndexRef.current = currentIndex;
    }, [currentIndex, elementRefs, scrollOffset]);
};
